import { useEffect, useState } from "react";
import { Loader2, X, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import api, { inr, fmtDateTime, fmtErr } from "../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none";

export function AdminWalletAdjust({ target, onClose, onDone }) {
  const [form, setForm] = useState({ type: "credit", amount: "", note: "" });
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/wallets/${target.user_id}/transactions`).then(({ data }) => setTxns(data)).catch(() => {});
  }, [target.user_id]);

  const amt = Number(form.amount) || 0;
  const newBalance = form.type === "credit" ? (target.balance || 0) + amt : (target.balance || 0) - amt;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/wallets/${target.user_id}/adjust`, { type: form.type, amount: amt, note: form.note || "Admin adjustment" });
      toast.success(`Wallet ${form.type === "credit" ? "credited" : "debited"} ${inr(amt)} — new balance ${inr(data.balance)}`);
      onDone?.();
      onClose();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="wallet-adjust-modal">
      <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-shipany-navy">Add / Adjust Balance</h2>
            <p className="text-xs text-slate-400">{target.name} · {target.email}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Current balance</span>
          <span className="mono text-lg font-bold text-shipany-navy" data-testid="adjust-current-balance">{inr(target.balance || 0)}</span>
        </div>

        <div className="mt-3 flex gap-3">
          {[["credit", "Add Money", ArrowDownCircle], ["debit", "Deduct", ArrowUpCircle]].map(([t, label, Icon]) => (
            <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`adjust-type-${t}`}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl border-2 py-2.5 text-sm font-bold ${form.type === t ? (t === "credit" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-red-500 bg-red-50 text-red-600") : "border-slate-200 text-slate-400"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <input data-testid="adjust-amount" type="number" min="1" step="0.01" required placeholder="Amount ₹" className={`${inputCls} mono mt-3`} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <div className="mt-2 flex gap-2">
          {[500, 1000, 5000].map((v) => (
            <button type="button" key={v} onClick={() => setForm({ ...form, amount: String(v) })} data-testid={`adjust-chip-${v}`}
              className="flex-1 rounded-lg border border-slate-200 py-1.5 text-xs font-bold text-slate-500 hover:border-shipany-blue hover:text-shipany-blue">+₹{v.toLocaleString("en-IN")}</button>
          ))}
        </div>
        <input data-testid="adjust-note" placeholder="Note (reason — visible to customer)" className={`${inputCls} mt-3`} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />

        {amt > 0 && (
          <div className={`mt-3 flex items-center justify-between rounded-xl px-4 py-3 ${newBalance < 0 ? "bg-red-50" : "bg-emerald-50"}`} data-testid="adjust-preview">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">New balance</span>
            <span className={`mono text-lg font-bold ${newBalance < 0 ? "text-red-600" : "text-emerald-700"}`}>{inr(newBalance)}</span>
          </div>
        )}

        <button data-testid="adjust-submit-btn" disabled={busy} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-50 glow-blue">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {form.type === "credit" ? "Add Balance" : "Apply Deduction"}
        </button>

        {txns.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="adjust-recent-txns">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Recent wallet activity</p>
            <div className="mt-2 max-h-36 space-y-1.5 overflow-y-auto">
              {txns.map((t) => (
                <div key={t.txn_id} className="flex items-center justify-between text-xs">
                  <span className="truncate text-slate-500" title={t.note}>{t.note?.slice(0, 42)}</span>
                  <span className={`mono ml-2 shrink-0 font-bold ${t.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>{t.type === "credit" ? "+" : "−"}{inr(t.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
